import { resolveMidnightApiBase } from '@1delta/data-sdk'
import type {
  MidnightBook,
  MidnightBookSource,
  MidnightBookTop,
  MidnightBookLevel,
} from './types'

/** Default hosted Midnight API (see @morpho-org/midnight-sdk MidnightApi). */
export const DEFAULT_MIDNIGHT_API = 'https://api.morpho.org/v0/midnight'

const LENDING_DEBUG = false
const warn = (...args: unknown[]) => {
  if (LENDING_DEBUG) console.warn('[midnight-api]', ...args)
}

type FetchLike = typeof fetch

/** Resolve a chain's Midnight API base URL (runtime override → config → hosted default). */
export function midnightApiBaseUrl(chainId: string): string {
  return resolveMidnightApiBase(chainId)
}

const toBig = (v: unknown): bigint => {
  try {
    if (v == null) return 0n
    if (typeof v === 'bigint') return v
    if (typeof v === 'number') return BigInt(Math.trunc(v))
    return BigInt(String(v))
  } catch {
    return 0n
  }
}

// Raw API price level: `{ tick, price, units, assets, count }` (snake_case,
// string-encoded). Field names mirror @morpho-org/midnight-sdk api/helpers
// `mapPriceLevel`. `assets` may be absent on some responses — derived as
// price·units (WAD) when missing.
function parseLevel(raw: any): MidnightBookLevel {
  const tick = toBig(raw?.tick)
  const units = toBig(raw?.units)
  let assets = toBig(raw?.assets)
  if (assets === 0n && units > 0n && raw?.price != null) {
    assets = (toBig(raw.price) * units) / 1_000_000_000_000_000_000n // price is WAD
  }
  return { tick, units, assets }
}

/**
 * Reduce both book sides to the best executable rate + aggregate depth. Order-
 * agnostic: min-rate bid = best borrow, max-rate ask = best supply. Because
 * tick↑ ⇒ price↑ ⇒ rate↓, "min rate over bids" is the MAX bid tick and
 * "max rate over asks" is the MIN ask tick.
 */
function reduceTop(bids: MidnightBookLevel[], asks: MidnightBookLevel[]): MidnightBookTop {
  let bestBorrowTick: bigint | undefined
  let bestSupplyTick: bigint | undefined
  let borrowDepthUnits = 0n
  let borrowDepthAssets = 0n
  let supplyDepthUnits = 0n
  let supplyDepthAssets = 0n

  for (const l of bids) {
    borrowDepthUnits += l.units
    borrowDepthAssets += l.assets
    // cheapest borrow = highest price = highest tick
    if (bestBorrowTick === undefined || l.tick > bestBorrowTick) bestBorrowTick = l.tick
  }
  for (const l of asks) {
    supplyDepthUnits += l.units
    supplyDepthAssets += l.assets
    // highest lend yield = lowest price = lowest tick
    if (bestSupplyTick === undefined || l.tick < bestSupplyTick) bestSupplyTick = l.tick
  }

  return {
    bestBorrowTick,
    bestSupplyTick,
    borrowDepthUnits,
    borrowDepthAssets,
    supplyDepthUnits,
    supplyDepthAssets,
  }
}

/**
 * Hosted-API book source. Reads `GET {base}/books/{marketId}` and reduces the
 * `asks`/`bids` price levels to a {@link MidnightBookTop}. This is the swappable
 * seam: a self-indexed mempool source can implement the same interface later.
 */
export class ApiBookSource implements MidnightBookSource {
  private readonly baseUrl: string
  private readonly fetchImpl: FetchLike

  constructor(baseUrl: string, fetchImpl: FetchLike = fetch) {
    this.baseUrl = baseUrl
    // Call fetch through globalThis. Storing the global `fetch` on the instance
    // and calling `this.fetchImpl(url)` sets `this` to the instance, which the
    // Cloudflare Workers runtime rejects with "Illegal invocation" (Node/undici
    // tolerates it — which is why this only breaks in the deployed/dev worker,
    // not in tests). A caller-supplied stub is unaffected by the `this` arg.
    this.fetchImpl = (input: any, init?: any) =>
      fetchImpl.call(globalThis, input, init)
  }

  async getBookTop(marketId: string): Promise<MidnightBookTop | null> {
    const url = `${this.baseUrl}/books/${marketId}`
    try {
      const res = await this.fetchImpl(url)
      if (!res.ok) {
        warn(`book ${marketId} → HTTP ${res.status}`)
        return null
      }
      const json: any = await res.json()
      const book = json?.data ?? json
      const bids = Array.isArray(book?.bids) ? book.bids.map(parseLevel) : []
      const asks = Array.isArray(book?.asks) ? book.asks.map(parseLevel) : []
      return reduceTop(bids, asks)
    } catch (e) {
      warn(`book ${marketId} fetch failed:`, (e as any)?.message ?? e)
      return null
    }
  }

  /**
   * Full ladder for a market — every level per side, best-first (unlike
   * `getBookTop`, which collapses to the best level + aggregate depth). Same
   * `GET {base}/books/{marketId}` fetch; empty levels are dropped and each side
   * is sorted so the best executable offer is first (bids by tick ↓ = cheapest
   * borrow, asks by tick ↑ = highest lend yield). The caller derives per-level
   * APR (from tick + TTM) and applies count/size filters.
   */
  async getBook(marketId: string): Promise<MidnightBook | null> {
    const url = `${this.baseUrl}/books/${marketId}`
    try {
      const res = await this.fetchImpl(url)
      if (!res.ok) {
        warn(`book ${marketId} → HTTP ${res.status}`)
        return null
      }
      const json: any = await res.json()
      const book = json?.data ?? json
      const bids = (Array.isArray(book?.bids) ? book.bids.map(parseLevel) : [])
        .filter((l: MidnightBookLevel) => l.units > 0n)
        .sort((a: MidnightBookLevel, b: MidnightBookLevel) =>
          a.tick < b.tick ? 1 : a.tick > b.tick ? -1 : 0,
        )
      const asks = (Array.isArray(book?.asks) ? book.asks.map(parseLevel) : [])
        .filter((l: MidnightBookLevel) => l.units > 0n)
        .sort((a: MidnightBookLevel, b: MidnightBookLevel) =>
          a.tick < b.tick ? -1 : a.tick > b.tick ? 1 : 0,
        )
      return { bids, asks }
    } catch (e) {
      // Surfaced unconditionally (unlike `warn`) — a thrown fetch here is a real
      // problem (network/binding), distinct from a 404 (delisted market → !res.ok).
      console.error(
        `[midnight getBook] ${url} threw:`,
        (e as any)?.message ?? e,
      )
      return null
    }
  }

  /**
   * ONE fetch → the full-depth aggregate top + a bounded best-first ladder slice
   * (top `maxLevels` per side). Same `/books/{marketId}` call as `getBookTop`, so
   * the public batch captures a chunk of the book for free (no extra request).
   */
  async getTopAndBook(
    marketId: string,
    maxLevels = 20,
  ): Promise<{ top: MidnightBookTop; book: MidnightBook } | null> {
    const url = `${this.baseUrl}/books/${marketId}`
    try {
      const res = await this.fetchImpl(url)
      if (!res.ok) {
        warn(`book ${marketId} → HTTP ${res.status}`)
        return null
      }
      const json: any = await res.json()
      const raw = json?.data ?? json
      const bids: MidnightBookLevel[] = (
        Array.isArray(raw?.bids) ? raw.bids.map(parseLevel) : []
      )
        .filter((l: MidnightBookLevel) => l.units > 0n)
        // cheapest borrow first = highest tick first
        .sort((a: MidnightBookLevel, b: MidnightBookLevel) =>
          a.tick < b.tick ? 1 : a.tick > b.tick ? -1 : 0,
        )
      const asks: MidnightBookLevel[] = (
        Array.isArray(raw?.asks) ? raw.asks.map(parseLevel) : []
      )
        .filter((l: MidnightBookLevel) => l.units > 0n)
        // highest lend yield first = lowest tick first
        .sort((a: MidnightBookLevel, b: MidnightBookLevel) =>
          a.tick < b.tick ? -1 : a.tick > b.tick ? 1 : 0,
        )
      // top aggregates FULL depth; book keeps only the best `maxLevels` per side.
      const top = reduceTop(bids, asks)
      const book: MidnightBook = {
        bids: bids.slice(0, maxLevels),
        asks: asks.slice(0, maxLevels),
      }
      return { top, book }
    } catch (e) {
      console.error(
        `[midnight getTopAndBook] ${url} threw:`,
        (e as any)?.message ?? e,
      )
      return null
    }
  }

  /**
   * Maker addresses per price level, keyed by tick string. `getBook` levels are
   * aggregated by tick and carry NO maker (a level can be several makers), so
   * this reads the individual offers from the `{side}/quote` endpoint (which the
   * book endpoint lacks) and groups their makers by tick. `assets` should be the
   * book's total depth so the quote returns EVERY offer (it returns none when the
   * target exceeds depth). Best-effort: `{}` on any failure.
   */
  async getOfferMakers(
    marketId: string,
    side: 'bids' | 'asks',
    assets: bigint,
  ): Promise<Record<string, string[]>> {
    const map: Record<string, string[]> = {}
    const url = `${this.baseUrl}/books/${marketId}/${side}/quote?assets=${assets}`
    try {
      const res = await this.fetchImpl(url)
      if (!res.ok) return map
      const json: any = await res.json()
      const d = json?.data ?? json
      const takeable: any[] = Array.isArray(d?.takeable_offers)
        ? d.takeable_offers
        : []
      for (const t of takeable) {
        const offer = t?.offer ?? {}
        const tick = String(offer?.tick ?? '')
        const maker = String(offer?.maker ?? '').toLowerCase()
        if (!tick || !/^0x[0-9a-f]{40}$/.test(maker)) continue
        ;(map[tick] ??= []).push(maker)
      }
    } catch {
      // best-effort — makers are a hover nicety, never block the ladder
    }
    return map
  }
}

/** Build the default (hosted-API) book source for a chain. */
export function createMidnightBookSource(
  chainId: string,
  fetchImpl: FetchLike = fetch,
): MidnightBookSource {
  return new ApiBookSource(midnightApiBaseUrl(chainId), fetchImpl)
}
